"use strict";
/**
 * Domain enums shared by the backend and every client.
 *
 * These are mirrored one-for-one by the Prisma enums in
 * `backend/prisma/schema.prisma`. Changing a member here without a matching
 * migration will drift the contract, so treat the two as a single unit.
 *
 * They are `const` objects rather than TS `enum`s so the values survive
 * `isolatedModules`, tree-shake cleanly, and can be iterated at runtime.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SupplierOrderStatus = exports.StockMovementType = exports.CheckoutPaymentOption = exports.OnlinePaymentProvider = exports.CampaignKind = exports.NotificationCategory = exports.CustomerNotificationType = exports.RecommendationPlacement = exports.Gender = exports.PortionUnit = exports.StockUnit = exports.Currency = exports.Weekday = exports.StorageProvider = exports.ReservationStatus = exports.FLOOR_PLAN = exports.TableShape = exports.WaiterCallStatus = exports.WaiterCallType = exports.KitchenTicketStatus = exports.NotificationStatus = exports.NotificationChannel = exports.TelegramEvent = exports.LoyaltyTransactionType = exports.PromoCodeType = exports.PromotionType = exports.HandoverStatus = exports.CourierTransactionType = exports.DeliveryStatus = exports.VehicleType = exports.CourierStatus = exports.CashTransactionType = exports.CashShiftStatus = exports.PaymentTransactionType = exports.PaymentTransactionStatus = exports.ONLINE_PAYMENT_METHODS = exports.PaymentMethod = exports.PaymentStatus = exports.OrderSource = exports.OrderType = exports.ORDER_STATUS_TRANSITIONS = exports.TERMINAL_ORDER_STATUSES = exports.OrderStatus = exports.SubscriptionStatus = exports.TenantStatus = void 0;
exports.canTransitionOrderStatus = canTransitionOrderStatus;
/* -------------------------------------------------------------------------- */
/* Tenant & subscription (TZ §4, §6)                                          */
/* -------------------------------------------------------------------------- */
exports.TenantStatus = {
    TRIAL: 'TRIAL',
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
    BLOCKED: 'BLOCKED',
};
exports.SubscriptionStatus = {
    TRIAL: 'TRIAL',
    ACTIVE: 'ACTIVE',
    PAST_DUE: 'PAST_DUE',
    EXPIRED: 'EXPIRED',
    CANCELLED: 'CANCELLED',
};
/* -------------------------------------------------------------------------- */
/* Orders (TZ §12)                                                            */
/* -------------------------------------------------------------------------- */
exports.OrderStatus = {
    NEW: 'NEW',
    ACCEPTED: 'ACCEPTED',
    PREPARING: 'PREPARING',
    READY: 'READY',
    WAITING_COURIER: 'WAITING_COURIER',
    COURIER_ASSIGNED: 'COURIER_ASSIGNED',
    ON_DELIVERY: 'ON_DELIVERY',
    DELIVERED: 'DELIVERED',
    CANCELLED: 'CANCELLED',
    REFUNDED: 'REFUNDED',
};
/** Statuses after which an order no longer moves. */
exports.TERMINAL_ORDER_STATUSES = [
    exports.OrderStatus.DELIVERED,
    exports.OrderStatus.CANCELLED,
    exports.OrderStatus.REFUNDED,
];
/**
 * Allowed status transitions. The order service validates against this map, so
 * a client cannot jump an order straight from NEW to DELIVERED.
 *
 * PICKUP and DINE_IN orders finish at READY -> DELIVERED (handed to the guest);
 * DELIVERY orders travel the courier path.
 */
exports.ORDER_STATUS_TRANSITIONS = {
    NEW: [exports.OrderStatus.ACCEPTED, exports.OrderStatus.CANCELLED],
    ACCEPTED: [exports.OrderStatus.PREPARING, exports.OrderStatus.CANCELLED],
    PREPARING: [exports.OrderStatus.READY, exports.OrderStatus.CANCELLED],
    READY: [exports.OrderStatus.WAITING_COURIER, exports.OrderStatus.DELIVERED, exports.OrderStatus.CANCELLED],
    WAITING_COURIER: [exports.OrderStatus.COURIER_ASSIGNED, exports.OrderStatus.CANCELLED],
    COURIER_ASSIGNED: [exports.OrderStatus.ON_DELIVERY, exports.OrderStatus.WAITING_COURIER, exports.OrderStatus.CANCELLED],
    ON_DELIVERY: [exports.OrderStatus.DELIVERED, exports.OrderStatus.CANCELLED],
    DELIVERED: [exports.OrderStatus.REFUNDED],
    CANCELLED: [],
    REFUNDED: [],
};
function canTransitionOrderStatus(from, to) {
    return exports.ORDER_STATUS_TRANSITIONS[from].includes(to);
}
exports.OrderType = {
    DELIVERY: 'DELIVERY',
    PICKUP: 'PICKUP',
    DINE_IN: 'DINE_IN',
    TABLE: 'TABLE',
};
/** Which client created the order — drives analytics splits on the dashboard. */
exports.OrderSource = {
    TELEGRAM_MINI_APP: 'TELEGRAM_MINI_APP',
    TELEGRAM_BOT: 'TELEGRAM_BOT',
    WEB: 'WEB',
    POS: 'POS',
    QR_TABLE: 'QR_TABLE',
    CALL_CENTER: 'CALL_CENTER',
    ADMIN: 'ADMIN',
};
exports.PaymentStatus = {
    UNPAID: 'UNPAID',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    REFUNDED: 'REFUNDED',
    PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
};
/* -------------------------------------------------------------------------- */
/* Payments (TZ §17, §35)                                                     */
/* -------------------------------------------------------------------------- */
exports.PaymentMethod = {
    CASH: 'CASH',
    CARD: 'CARD',
    TERMINAL: 'TERMINAL',
    CLICK: 'CLICK',
    PAYME: 'PAYME',
    QR: 'QR',
    BONUS: 'BONUS',
    /**
     * Paid online through Subo, which aggregates Atmos, Click and Payme behind
     * one hosted checkout. From the till's point of view it is one tender; which
     * provider settled it is recorded separately on the payment.
     */
    SUBO: 'SUBO',
};
/** Methods settled through an external provider webhook rather than at the till. */
exports.ONLINE_PAYMENT_METHODS = [
    exports.PaymentMethod.CLICK,
    exports.PaymentMethod.PAYME,
    exports.PaymentMethod.QR,
    exports.PaymentMethod.SUBO,
];
exports.PaymentTransactionStatus = {
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
    REFUNDED: 'REFUNDED',
};
exports.PaymentTransactionType = {
    CHARGE: 'CHARGE',
    REFUND: 'REFUND',
};
/* -------------------------------------------------------------------------- */
/* Cash register (TZ §19, §28)                                                */
/* -------------------------------------------------------------------------- */
exports.CashShiftStatus = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
};
exports.CashTransactionType = {
    SALE: 'SALE',
    REFUND: 'REFUND',
    /** Manual cash put into the drawer (e.g. float top-up). */
    CASH_IN: 'CASH_IN',
    /** Manual cash taken out (e.g. supplier payment). */
    CASH_OUT: 'CASH_OUT',
    /** Courier handing collected cash to the cashier. */
    COURIER_HANDOVER: 'COURIER_HANDOVER',
    /** Cash counted into the drawer when the shift opens. */
    OPENING_FLOAT: 'OPENING_FLOAT',
    /** Cash removed when the shift closes. */
    WITHDRAWAL: 'WITHDRAWAL',
};
/* -------------------------------------------------------------------------- */
/* Couriers & delivery (TZ §23-§28)                                           */
/* -------------------------------------------------------------------------- */
exports.CourierStatus = {
    OFFLINE: 'OFFLINE',
    AVAILABLE: 'AVAILABLE',
    ON_DELIVERY: 'ON_DELIVERY',
    BREAK: 'BREAK',
};
exports.VehicleType = {
    FOOT: 'FOOT',
    BICYCLE: 'BICYCLE',
    SCOOTER: 'SCOOTER',
    MOTORCYCLE: 'MOTORCYCLE',
    CAR: 'CAR',
};
exports.DeliveryStatus = {
    PENDING: 'PENDING',
    ASSIGNED: 'ASSIGNED',
    ACCEPTED: 'ACCEPTED',
    PICKED_UP: 'PICKED_UP',
    DELIVERED: 'DELIVERED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
};
exports.CourierTransactionType = {
    /** Cash collected from a customer on delivery. */
    CASH_RECEIVED: 'CASH_RECEIVED',
    /** Courier's own earning for the delivery. */
    DELIVERY_INCOME: 'DELIVERY_INCOME',
    EXPENSE: 'EXPENSE',
    /** Cash handed to the cashier — reduces the balance the courier owes. */
    HANDOVER: 'HANDOVER',
    ADJUSTMENT: 'ADJUSTMENT',
};
exports.HandoverStatus = {
    PENDING: 'PENDING',
    CONFIRMED: 'CONFIRMED',
    DISPUTED: 'DISPUTED',
    CANCELLED: 'CANCELLED',
};
/* -------------------------------------------------------------------------- */
/* Promotions & loyalty (TZ §10, §33, §34)                                    */
/* -------------------------------------------------------------------------- */
exports.PromotionType = {
    PERCENTAGE_DISCOUNT: 'PERCENTAGE_DISCOUNT',
    FIXED_DISCOUNT: 'FIXED_DISCOUNT',
    BUY_X_GET_Y: 'BUY_X_GET_Y',
    COMBO: 'COMBO',
    HAPPY_HOUR: 'HAPPY_HOUR',
    FREE_DELIVERY: 'FREE_DELIVERY',
};
exports.PromoCodeType = {
    PERCENTAGE: 'PERCENTAGE',
    FIXED: 'FIXED',
    FREE_DELIVERY: 'FREE_DELIVERY',
};
exports.LoyaltyTransactionType = {
    EARN: 'EARN',
    REDEEM: 'REDEEM',
    EXPIRE: 'EXPIRE',
    ADJUSTMENT: 'ADJUSTMENT',
    REFERRAL_BONUS: 'REFERRAL_BONUS',
};
/* -------------------------------------------------------------------------- */
/* Telegram (TZ §15)                                                          */
/* -------------------------------------------------------------------------- */
/**
 * Events a branch can route to a specific Telegram chat + topic.
 * The admin picks a chat/topic per event, per branch.
 */
exports.TelegramEvent = {
    NEW_ORDER: 'NEW_ORDER',
    ORDER_ACCEPTED: 'ORDER_ACCEPTED',
    ORDER_CANCELLED: 'ORDER_CANCELLED',
    PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    KITCHEN_READY: 'KITCHEN_READY',
    COURIER_ASSIGNED: 'COURIER_ASSIGNED',
    ORDER_DELIVERED: 'ORDER_DELIVERED',
    DELIVERY_PROBLEM: 'DELIVERY_PROBLEM',
    WAITER_CALL: 'WAITER_CALL',
    SHIFT_CLOSED: 'SHIFT_CLOSED',
};
/* -------------------------------------------------------------------------- */
/* Notifications (TZ §38)                                                     */
/* -------------------------------------------------------------------------- */
exports.NotificationChannel = {
    TELEGRAM: 'TELEGRAM',
    PUSH: 'PUSH',
    SMS: 'SMS',
    EMAIL: 'EMAIL',
};
exports.NotificationStatus = {
    PENDING: 'PENDING',
    SENT: 'SENT',
    FAILED: 'FAILED',
    SKIPPED: 'SKIPPED',
};
/* -------------------------------------------------------------------------- */
/* Kitchen (TZ §21, §22)                                                      */
/* -------------------------------------------------------------------------- */
exports.KitchenTicketStatus = {
    QUEUED: 'QUEUED',
    IN_PROGRESS: 'IN_PROGRESS',
    READY: 'READY',
    SERVED: 'SERVED',
    CANCELLED: 'CANCELLED',
};
/* -------------------------------------------------------------------------- */
/* Tables / waiter calls (TZ §29, §30)                                        */
/* -------------------------------------------------------------------------- */
exports.WaiterCallType = {
    CALL_WAITER: 'CALL_WAITER',
    REQUEST_BILL: 'REQUEST_BILL',
};
exports.WaiterCallStatus = {
    PENDING: 'PENDING',
    ACKNOWLEDGED: 'ACKNOWLEDGED',
    RESOLVED: 'RESOLVED',
    CANCELLED: 'CANCELLED',
};
/** How the floor plan draws a table. */
exports.TableShape = {
    SQUARE: 'SQUARE',
    ROUND: 'ROUND',
    RECT: 'RECT',
};
/** The floor-plan canvas in plan units, whatever the screen draws it at. */
exports.FLOOR_PLAN = { width: 1000, height: 625 };
/**
 * BOOKED → SEATED → COMPLETED, or BOOKED → CANCELLED / NO_SHOW. The last
 * three are final.
 */
exports.ReservationStatus = {
    BOOKED: 'BOOKED',
    SEATED: 'SEATED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
    NO_SHOW: 'NO_SHOW',
};
/* -------------------------------------------------------------------------- */
/* Storage (TZ §39)                                                           */
/* -------------------------------------------------------------------------- */
exports.StorageProvider = {
    LOCAL: 'LOCAL',
    S3: 'S3',
    MINIO: 'MINIO',
};
/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */
/** ISO-8601 weekday numbering: 1 = Monday … 7 = Sunday. */
exports.Weekday = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7,
};
exports.Currency = {
    UZS: 'UZS',
    USD: 'USD',
};
/* -------------------------------------------------------------------------- */
/* Inventory (TZ 29-31)                                                       */
/* -------------------------------------------------------------------------- */
/** How a stock item is measured. Recipe quantities use the item's own unit. */
exports.StockUnit = {
    KG: 'KG',
    G: 'G',
    L: 'L',
    ML: 'ML',
    PCS: 'PCS',
};
/* -------------------------------------------------------------------------- */
/* Storefront (TZ 48)                                                         */
/* -------------------------------------------------------------------------- */
/** One portion as printed on a menu card: 450 g, 300 ml, 6 pcs. */
exports.PortionUnit = {
    G: 'G',
    ML: 'ML',
    PCS: 'PCS',
};
exports.Gender = {
    MALE: 'MALE',
    FEMALE: 'FEMALE',
};
exports.RecommendationPlacement = {
    /** "Sizga yoqishi mumkin" inside a product's sheet. */
    PRODUCT_DETAIL: 'PRODUCT_DETAIL',
    /** "Yana nimadir kerakmi?" on the cart page. */
    CART: 'CART',
};
exports.CustomerNotificationType = {
    ORDER_RECEIVED: 'ORDER_RECEIVED',
    ORDER_ACCEPTED: 'ORDER_ACCEPTED',
    ORDER_PREPARING: 'ORDER_PREPARING',
    ORDER_READY: 'ORDER_READY',
    ORDER_ON_DELIVERY: 'ORDER_ON_DELIVERY',
    ORDER_DELIVERED: 'ORDER_DELIVERED',
    ORDER_CANCELLED: 'ORDER_CANCELLED',
    PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    NEW_PRODUCT: 'NEW_PRODUCT',
    PROMOTION: 'PROMOTION',
    PROMO_CODE: 'PROMO_CODE',
    SERVICE: 'SERVICE',
};
/**
 * Kept apart so that withdrawing marketing consent can never switch off an
 * order update, and so a marketing send can never reach someone who did not
 * opt in.
 */
exports.NotificationCategory = {
    TRANSACTIONAL: 'TRANSACTIONAL',
    MARKETING: 'MARKETING',
    SERVICE: 'SERVICE',
};
exports.CampaignKind = {
    NEW_PRODUCT: 'NEW_PRODUCT',
    PROMOTION: 'PROMOTION',
    PROMO_CODE: 'PROMO_CODE',
    ANNOUNCEMENT: 'ANNOUNCEMENT',
};
/**
 * The providers Subo can be asked to open (docs.subo.uz, `POST /orders`
 * `provider`). Not a database enum: it is a string column in a settings table
 * and on the order, guarded by a CHECK.
 */
exports.OnlinePaymentProvider = {
    /** Bank card through Atmos — the one path that saves a card for next time. */
    ATMOS: 'ATMOS',
    CLICK: 'CLICK',
    PAYME: 'PAYME',
};
/** What the customer picks at checkout: cash, or one online provider. */
exports.CheckoutPaymentOption = {
    CASH: 'CASH',
    ...exports.OnlinePaymentProvider,
};
exports.StockMovementType = {
    /** Delivery from a supplier. Raises the level. */
    RECEIPT: 'RECEIPT',
    /** Used making an order. Lowers it, written automatically. */
    CONSUMPTION: 'CONSUMPTION',
    /** Spoiled, dropped, burnt. Reported separately -- waste is what a manager
        acts on, and burying it in consumption hides it. */
    WASTE: 'WASTE',
    /** A stock-take correcting the books to the shelf. Carries its own sign. */
    ADJUSTMENT: 'ADJUSTMENT',
    TRANSFER_OUT: 'TRANSFER_OUT',
    TRANSFER_IN: 'TRANSFER_IN',
};
/** A purchase order: sent, then received into stock or cancelled. */
exports.SupplierOrderStatus = {
    SENT: 'SENT',
    RECEIVED: 'RECEIVED',
    CANCELLED: 'CANCELLED',
};
//# sourceMappingURL=enums.js.map